import { useEffect, useMemo, useRef } from 'react';
import { useField, useForm, SubmitResult } from '@shopify/react-form';
import gettext from '@sdk/lang/gettext';
import useSlingApi from '@sdk/api/useSlingApi';
import useToast from '@sdk/common/toast/useToast';
import getErrorMessages from '@sdk/utils/getErrorMessages';
import validateNumber, {
  ValidateOptions,
  ValidNumberOption,
} from '@sdk/utils/validation/validateNumber';
import type { OrgSettings } from '@sdk/authentication/types/OrganisationType';
import type { PersonasDict } from '@sdk/personaCollection/types/PersonasDict';

import { decorateSubmit } from '@web/forms/utils/decorateSubmit';
import { FormSubmitStatuses } from '@web/forms/types';
import { useFormValidation } from '@web/common/hooks/useFormValidation';

import type { UpdateOrgSettings } from '../../onboardingChecklist.types';
import normalizeOrgSettings from '../../../../settings/helpers/normalizeOrgSettings';
import preSaveUserOrgSettingsHandler from '../../../../settings/helpers/preSaveUserOrgSettingsHandler';
import {
  MIN_FEET_DISTANCE,
  MIN_METERS_DISTANCE,
} from '../../../../settings/components/timeclock/Geofencing.consts';
import { ONBOARDING_TOAST_ID } from '../../onboardingChecklist.consts';

type Props = {
  orgSettings: OrgSettings;
  personasDict: PersonasDict;
  close: () => void;
  updateOrgSettings: UpdateOrgSettings;
};

export const useTimeclockForm = ({
  orgSettings,
  personasDict,
  close,
  updateOrgSettings,
}: Props) => {
  const userOrgSettings = useMemo(() => orgSettings, [orgSettings]);
  const api = useSlingApi();
  const { showSuccessToast } = useToast();

  const submitPromise = useRef<Promise<SubmitResult>>();
  const imperialUnits = useField(userOrgSettings.imperialUnits);
  const {
    fields: timeClockFields,
    dirty,
    reset,
    validate,
    submit: formSubmit,
    submitErrors,
  } = useForm({
    fields: {
      clockInFromWebAndApps: useField(userOrgSettings.clockInFromWebAndApps),
      clockInEnabled: useField(userOrgSettings.clockInEnabled),
      clockInGeoRequired: useField(userOrgSettings.clockInGeoRequired),
      imperialUnits,
      clockInMaxDistance: useField(
        {
          value: userOrgSettings.clockInMaxDistance,
          validates: (value) => {
            if (
              !validateNumber(value, {
                [ValidateOptions.integer]: true,
                [ValidateOptions.validNumberGreaterOrEqualThan]: imperialUnits.value
                  ? MIN_FEET_DISTANCE
                  : MIN_METERS_DISTANCE,
              })
            ) {
              return `Within ${
                imperialUnits.value ? `${MIN_FEET_DISTANCE} feet` : `${MIN_METERS_DISTANCE} meters`
              } radius of location`;
            }
          },
        },
        [imperialUnits.value]
      ),
      clockInEarlyEnabled: useField(userOrgSettings.clockInEarlyEnabled),
      clockInEarlyMinutes: useField({
        value: userOrgSettings.clockInEarlyMinutes,
        validates: (value) => {
          if (
            !validateNumber(value, {
              [ValidateOptions.validNumber]: ValidNumberOption.nonNegative,
              [ValidateOptions.integer]: true,
            })
          ) {
            return 'clockInEarlyMinutes invalid';
          }
        },
      }),
      clockInAutoClockoutEnabled: useField(userOrgSettings.clockInAutoClockoutEnabled),
      clockInAutoClockoutMinutes: useField({
        value: userOrgSettings.clockInAutoClockoutMinutes,
        validates: (value) => {
          if (
            !validateNumber(value, {
              [ValidateOptions.validNumber]: ValidNumberOption.nonNegative,
              [ValidateOptions.integer]: true,
            })
          ) {
            return 'clockInAutoClockoutMinutes invalid';
          }
        },
      }),
    },
    onSubmit: async (fieldValues) => {
      const payload = preSaveUserOrgSettingsHandler({
        ...userOrgSettings,
        ...fieldValues,
      });

      try {
        if (dirty) {
          const { data } = await api.auth.setUserOrgSettings(payload);
          const normalizedSettings = normalizeOrgSettings(data, personasDict);
          updateOrgSettings({ settings: normalizedSettings });
        }

        close();
        showSuccessToast(gettext('Time clock rules set'), {
          containerId: ONBOARDING_TOAST_ID,
        });

        submitPromise.current = Promise.resolve({ status: FormSubmitStatuses.success });
        return submitPromise.current;
      } catch (error) {
        const [errorMsg] = getErrorMessages(error);

        submitPromise.current = Promise.resolve({
          status: FormSubmitStatuses.fail,
          errors: [{ message: errorMsg }],
        });
        return submitPromise.current;
      }
    },
  });

  const submit = decorateSubmit(formSubmit, submitPromise);

  useEffect(() => {
    timeClockFields.clockInMaxDistance.runValidation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imperialUnits.value]);

  const { isValid } = useFormValidation(timeClockFields);

  return {
    timeClockFields,
    submit,
    reset,
    isValid,
    submitErrors,
    validate,
  };
};
